import { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { Box, Grid, IconButton, Typography, CircularProgress, Snackbar, Alert, AlertTitle } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import { getPublicServiceDetail, getCurrentProfile } from '../../actions/profile';
import { getQuoteDetail } from '../../actions/quote';
import { createBooking, validarPromo } from '../../actions/bookings';
import { getDurationMinutes } from '../../components/booking/bookingSlotUtils';
import { fechaLocal, instanteDeCita } from '../../components/booking/horaMexico';
import { montoDeReserva } from '../../components/booking/montoReserva';
import { pagosDisponibles } from '../../utils/pagos';
import { motivoSinMetodo } from '../../utils/reglasPago';

// Booking Components
import BookingOptions from '../../components/booking/BookingOptions';
import BookingPayment from '../../components/booking/BookingPayment';
import BookingSummary from '../../components/booking/BookingSummary';
import BookingDateTime from '../../components/booking/BookingDateTime';
import BookingMessage from '../../components/booking/BookingMessage';
import BookingAddress from '../../components/booking/BookingAddress';
import BookingInvoice from '../../components/booking/BookingInvoice';
import BookingConfirmation from '../../components/booking/BookingConfirmation';

const BookingContainer = () => {
  const { userId, serviceId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const quoteId = searchParams.get('quoteId');

  const [loading, setLoading] = useState(true);
  const [service, setService] = useState(null);
  const [profile, setProfile] = useState(null);
  const [ownerUser, setOwnerUser] = useState(null);
  // Quién imparte el servicio (en un grupo o negocio puede ser otra persona): es a quien se muestra en el resumen.
  // La reserva sigue siendo con el proveedor (ownerUser), que es quien la recibe y la acepta.
  const [imparte, setImparte] = useState(null);
  const [quote, setQuote] = useState(null);

  // Booking State
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedTime, setSelectedTime] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState('Español');
  const [selectedModality, setSelectedModality] = useState('Remoto');
  const [paymentMethod, setPaymentMethod] = useState('card');
  const stripeCardRef = useRef(null); // acceso imperativo al CardElement de Stripe
  const [bookingNote, setBookingNote] = useState('');
  // A domicilio: _id de una dirección del perfil (una nueva se guarda primero en el perfil desde BookingAddress)
  const [selectedAddressId, setSelectedAddressId] = useState('');
  const [selectedInvoiceId, setSelectedInvoiceId] = useState('');
  const [newInvoice, setNewInvoice] = useState({
    nombre: '', rfc: '', razonSocial: '', direccionFiscal: '', cp: '', regimenFiscal: '', usoCFDI: ''
  });
  const [requireInvoice, setRequireInvoice] = useState(false);

  // Código promocional
  const [promoCodigo, setPromoCodigo] = useState('');
  const [promoAplicado, setPromoAplicado] = useState(null); // { codigo, descuentoPct }
  const [promoError, setPromoError] = useState('');
  const [promoLoading, setPromoLoading] = useState(false);

  // Confirmation state
  const [confirmed, setConfirmed] = useState(false);
  const [bookingResult, setBookingResult] = useState(null);
  // Con qué quedó pagada la reserva, como lo decide el servidor: con paymentMethodId (tarjeta o wallet) es 'card', si no
  // SPEI/OXXO, y null si no lleva pago en línea. No sirve `paymentMethod`: la wallet se usa aunque esté marcado SPEI u OXXO.
  const [metodoReserva, setMetodoReserva] = useState(null);
  const [isConfirming, setIsConfirming] = useState(false);
  const [validationError, setValidationError] = useState('');
  // Campo obligatorio faltante para pintarlo de rojo: horario|direccion|numero|nombre|expiracion|cvv
  const [campoError, setCampoError] = useState('');
  // Marca el mensaje del alert y el campo que hay que resaltar.
  const marcarError = (msg, campo = '') => { setValidationError(msg); setCampoError(campo); };

  const dispatch = useDispatch();
  const authUser = useSelector(state => state.user.user);
  const userProfileClient = useSelector(state => state.profile.profile);

  useEffect(() => {
    const fetchServiceAndQuote = async () => {
      try {
        const data = await getPublicServiceDetail(userId, serviceId);
        if (data && data.service) {
          setService(data.service);
          setProfile(data.profile);
          setOwnerUser(data.ownerUser);
          setImparte(Array.isArray(data.imparten) && data.imparten[0]?._id ? data.imparten[0] : null);

          if (data.service.idiomas && data.service.idiomas.length > 0) {
            setSelectedLanguage(data.service.idiomas[0]);
          }
          if (data.service.modalidades && data.service.modalidades.length > 0) {
            setSelectedModality(data.service.modalidades[0]);
          }
        }

        if (quoteId) {
          const quoteData = await dispatch(getQuoteDetail(quoteId));
          if (quoteData) {
            setQuote(quoteData);
            setSelectedLanguage(quoteData.idioma || 'Español');
            const ofrecidas = quoteData.modalidades?.length
              ? quoteData.modalidades
              : [quoteData.modalidad].filter(Boolean);
            setSelectedModality(ofrecidas[0] || 'Remoto');
          }
        }
      } catch (err) {
        console.error("Error fetching service or quote for booking:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchServiceAndQuote();
    dispatch(getCurrentProfile());
  }, [userId, serviceId, quoteId, dispatch]);

  useEffect(() => {
    if (authUser && String(authUser._id) === String(userId)) {
      navigate('/');
    }
  }, [authUser, userId, navigate]);

  // Al seleccionar horario se quita su marca de error (si la tenía).
  const handleTimeSelect = (t) => {
    setSelectedTime(t);
    if (campoError === 'horario') setCampoError('');
  };

  // Igual con la dirección de un servicio a domicilio.
  const handleAddressSelect = (id) => {
    setSelectedAddressId(id);
    if (campoError === 'direccion') setCampoError('');
  };

  const handleAplicarPromo = async () => {
    const cod = promoCodigo.trim();
    if (!cod) return;
    setPromoLoading(true);
    setPromoError('');
    const catId = service?.categoria?._id || service?.categoria || '';
    const res = await validarPromo(cod, catId);
    if (res?.valido) {
      setPromoAplicado({
        codigo: res.codigo,
        tipo: res.tipo,
        descuentoPct: res.descuentoPct,
        montoFijo: res.montoFijo,
        montoMinimo: res.montoMinimo
      });
      setPromoError('');
    } else {
      setPromoAplicado(null);
      setPromoError(res?.msg || 'Código no válido o expirado.');
    }
    setPromoLoading(false);
  };

  const handleQuitarPromo = () => {
    setPromoAplicado(null);
    setPromoCodigo('');
    setPromoError('');
  };

  // Lo que tiene precio se paga en la plataforma (de ahí sale su comisión): se pide con qué pagar. Si esta compilación no trae la llave de
  // Stripe no hay con qué, y no se deja reservar (antes se reservaba sin pago). Con cotización manda el precio NEGOCIADO (es lo que el
  // backend cobrará). El cargo NO ocurre al reservar: se procesa cuando el proveedor acepta.
  const costoBase = quote ? parseFloat(quote.costo) : parseFloat(service?.costoServicio);
  const requierePago = costoBase > 0;
  const sinPagos = requierePago && !pagosDisponibles();

  // SPEI y OXXO solo cuando se pueden pagar a tiempo (y OXXO hasta su tope), con el total que cobrará el servidor
  const instante = selectedTime ? instanteDeCita(fechaLocal(selectedDate), selectedTime) : null;
  const totalACobrar = service ? montoDeReserva({
    costoServicio: costoBase,
    tipoCosto: quote ? '' : service.tipoCosto,
    duracionMinutos: getDurationMinutes(service.duracionEstimada, service.unidadDuracion),
  }, promoAplicado).total : 0;
  const noDisponibles = {
    spei: motivoSinMetodo('spei', { total: totalACobrar, instante }),
    oxxo: motivoSinMetodo('oxxo', { total: totalACobrar, instante }),
  };
  // Si al cambiar el día (o el total) el método elegido ya no se puede, regresa a tarjeta
  const metodoNoDisponible = noDisponibles[paymentMethod] || '';
  useEffect(() => {
    if (metodoNoDisponible) setPaymentMethod('card');
  }, [metodoNoDisponible]);

  // Validaciones previas al pago (comunes a tarjeta, SPEI/OXXO y wallet). Devuelve false
  // y marca el campo si falta algo. La wallet la usa antes de abrir la hoja nativa.
  const validarReserva = () => {
    if (!selectedTime) {
      marcarError('Por favor selecciona un horario disponible.', 'horario');
      return false;
    }
    // Tiene que seguir en el perfil: si no, el servidor no la encuentra y la reserva queda sin dirección
    if (selectedModality === 'Domicilio' && !(userProfileClient?.direcciones || []).some(d => d._id === selectedAddressId)) {
      marcarError('Elige o agrega la dirección donde será el servicio.', 'direccion');
      return false;
    }
    return true;
  };

  // Arma los datos fiscales (si aplica) y crea la reserva. `pago` ya trae el método
  // resuelto: { paymentMethodId } para tarjeta/wallet, o { metodoPago } para SPEI/OXXO.
  const crearReservaConPago = async (pago) => {
    // Factura: si la solicita, se envían los datos fiscales (los del formulario nuevo
    // o los del perfil fiscal guardado que haya elegido) para que el superadmin emita el CFDI
    let datosFactura = null;
    if (requireInvoice) {
      if (selectedInvoiceId && selectedInvoiceId !== 'new') {
        const saved = (userProfileClient?.facturacion || []).find(f => f._id === selectedInvoiceId);
        datosFactura = saved
          ? {
              nombre: saved.nombre || '', rfc: saved.rfc || '',
              razonSocial: saved.razonSocial || saved.nombre || '',
              direccionFiscal: saved.direccionFiscal || '', cp: saved.cp || '',
              regimenFiscal: saved.regimenFiscal || '', usoCFDI: saved.usoCFDI || ''
            }
          : newInvoice;
      } else {
        datosFactura = newInvoice;
      }
    }

    const duracionMinutos = getDurationMinutes(service.duracionEstimada, service.unidadDuracion);
    const result = await dispatch(createBooking({
      servicio: service._id,
      proveedor: ownerUser?._id,
      quoteId: quote?._id,
      fecha: fechaLocal(selectedDate),   // el día elegido (toISOString daría el de UTC: desde las 6 p. m., mañana)
      hora: selectedTime,
      duracionMinutos: duracionMinutos || 60,
      modalidad: selectedModality,
      // El servidor guarda una foto de esa dirección del perfil: es la que ve el proveedor
      ...(selectedModality === 'Domicilio' ? { direccionId: selectedAddressId } : {}),
      nota: bookingNote,
      codigoPromo: promoAplicado?.codigo || '',
      requiereFactura: requireInvoice,
      datosFactura,
      ...pago
    }));

    if (result?.success) {
      setBookingResult(result.booking);
      setMetodoReserva(pago.paymentMethodId ? 'card' : (pago.metodoPago || null));
      setConfirmed(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
      return true;
    }
    setValidationError(result?.msg || 'Error al confirmar la reserva. Intenta de nuevo.');
    return false;
  };

  const handleConfirm = async () => {
    if (sinPagos || !validarReserva()) return;

    setIsConfirming(true);
    try {
      // Stripe: guarda/autentica la tarjeta con un SetupIntent y devuelve el paymentMethodId.
      let pago = {};
      if (requierePago) {
        if (paymentMethod === 'card') {
          try {
            const paymentMethodId = await stripeCardRef.current.confirmar();
            pago = { paymentMethodId };
          } catch (tokenErr) {
            setValidationError(tokenErr.message || 'No se pudo validar la tarjeta.');
            setIsConfirming(false);
            return;
          }
        } else {
          pago = { metodoPago: paymentMethod };
        }
      }

      await crearReservaConPago(pago);
    } catch (err) {
      setValidationError('Error al confirmar la reserva. Intenta de nuevo.');
    } finally {
      setIsConfirming(false);
    }
  };

  // Apple/Google Pay: la hoja nativa ya autorizó y guardó la tarjeta (pm_...). El botón
  // de la wallet es su propio CTA, así que aquí solo creamos la reserva con ese método.
  const handleWalletPago = async (paymentMethodId) => {
    setIsConfirming(true);
    try {
      await crearReservaConPago({ paymentMethodId });
    } catch (err) {
      setValidationError('Error al confirmar la reserva. Intenta de nuevo.');
    } finally {
      setIsConfirming(false);
    }
  };

  if (loading || !service) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (confirmed) {
    return (
      <BookingConfirmation
        booking={bookingResult}
        service={service}
        ownerUser={imparte || ownerUser}
        selectedDate={selectedDate}
        selectedTime={selectedTime}
        selectedLanguage={selectedLanguage}
        selectedModality={selectedModality}
        metodoPago={metodoReserva}
        promo={promoAplicado}
      />
    );
  }

  return (
    <Grid maxWidth="xl" sx={{ mt: 12, mb: 8 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 4 }}>
        <IconButton
          onClick={() => navigate(-1)}
          sx={{
            mr: 2,
            bgcolor: 'sutil',
            color: 'primary.main',
            '&:hover': { bgcolor: 'divider' }
          }}
        >
          <ArrowBackIosIcon sx={{ fontSize: 16, ml: 0.5 }} />
        </IconButton>
        <Typography variant="subtitle1" fontWeight="bold" color="primary.main" sx={{ cursor: 'pointer' }} onClick={() => navigate(-1)}>
          Atrás
        </Typography>
      </Box>

      <Grid container spacing={1}>
        <Grid size={{ xs: 12, md: 7, lg: 8 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
            <BookingDateTime
              selectedDate={selectedDate}
              onDateSelect={setSelectedDate}
              selectedTime={selectedTime}
              onTimeSelect={handleTimeSelect}
              service={service}
              error={campoError === 'horario'}
            />

            <BookingOptions
              languages={quote ? [quote.idioma] : (service?.idiomas || ['Español'])}
              modalities={quote
                ? (quote.modalidades?.length ? quote.modalidades : [quote.modalidad].filter(Boolean))
                : (service?.modalidades || ['Remoto'])}
              selectedLanguage={selectedLanguage}
              setSelectedLanguage={setSelectedLanguage}
              selectedModality={selectedModality}
              setSelectedModality={setSelectedModality}
              disabled={!!quote}
              modalityDisabled={!!quote && (quote.modalidades?.length || (quote.modalidad ? 1 : 0)) <= 1}
            />

            {selectedModality === 'Domicilio' && (
              <BookingAddress
                addresses={userProfileClient?.direcciones || []}
                selectedAddressId={selectedAddressId}
                setSelectedAddressId={handleAddressSelect}
                error={campoError === 'direccion'}
              />
            )}

            <BookingMessage
              value={bookingNote}
              onChange={setBookingNote}
            />

            {requierePago && (sinPagos ? (
              <Alert severity="warning" sx={{ borderRadius: '12px' }}>
                <AlertTitle sx={{ fontWeight: 700 }}>Por ahora no se puede reservar</AlertTitle>
                Las reservas se pagan en la plataforma y en este momento el pago en línea no está disponible. Intenta más tarde.
              </Alert>
            ) : (
              <BookingPayment
                paymentMethod={paymentMethod}
                setPaymentMethod={setPaymentMethod}
                stripeCardRef={stripeCardRef}
                onWalletPago={handleWalletPago}
                validarPago={validarReserva}
                onWalletError={setValidationError}
                noDisponibles={noDisponibles}
              />
            ))}

            <Box sx={{
              p: 4,
              borderRadius: '24px',
              bgcolor: 'white',
              border: 1, borderColor: 'sutil',
            }}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: requireInvoice ? 4 : 0
              }}>
                <Typography variant="h6" fontWeight="900" color="primary.main">
                  ¿Requieres factura?
                </Typography>
                <Box
                  component="span"
                  onClick={() => setRequireInvoice(!requireInvoice)}
                  sx={{
                    width: 54,
                    height: 28,
                    borderRadius: '20px',
                    bgcolor: requireInvoice ? 'primary.main' : 'divider',
                    position: 'relative',
                    cursor: 'pointer',
                    transition: '0.3s'
                  }}
                >
                  <Box sx={{
                    width: 22,
                    height: 22,
                    borderRadius: '50%',
                    bgcolor: 'white',
                    position: 'absolute',
                    top: 3,
                    left: requireInvoice ? 29 : 3,
                    transition: '0.3s',
                    boxShadow: '0px 2px 4px rgba(0,0,0,0.1)'
                  }} />
                </Box>
              </Box>

              {requireInvoice && (
                <BookingInvoice
                  facturacion={userProfileClient?.facturacion || []}
                  selectedInvoiceId={selectedInvoiceId}
                  setSelectedInvoiceId={setSelectedInvoiceId}
                  newInvoice={newInvoice}
                  setNewInvoice={setNewInvoice}
                />
              )}
            </Box>
          </Box>
        </Grid>

        <Grid size={{ xs: 12, md: 5, lg: 4 }}>
          <BookingSummary
            service={service}
            profile={profile}
            ownerUser={imparte || ownerUser}
            selectedDate={selectedDate}
            selectedTime={selectedTime}
            selectedLanguage={selectedLanguage}
            selectedModality={selectedModality}
            paymentMethod={paymentMethod}
            quote={quote}
            onConfirm={handleConfirm}
            isConfirming={isConfirming}
            puedeReservar={!sinPagos}
            promoCodigo={promoCodigo}
            setPromoCodigo={setPromoCodigo}
            promoAplicado={promoAplicado}
            promoError={promoError}
            promoLoading={promoLoading}
            onAplicarPromo={handleAplicarPromo}
            onQuitarPromo={handleQuitarPromo}
          />
        </Grid>
      </Grid>

      <Snackbar
        open={!!validationError}
        autoHideDuration={4000}
        onClose={() => setValidationError('')}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity="error" onClose={() => setValidationError('')}
          sx={{
            borderRadius: '12px', fontWeight: 700, fontSize: '1rem', py: 1.75, px: 3,
            alignItems: 'center', boxShadow: '0 10px 28px rgba(0,0,0,0.15)',
            '& .MuiAlert-icon': { fontSize: 28 }
          }}>
          {validationError}
        </Alert>
      </Snackbar>
    </Grid>
  );
};

export default BookingContainer;
